// Breadcrumbs.tsx
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { MenuRouteItem, MenuRoute } from "../menu/types";
import type { UrlObject } from "url";

interface BreadcrumbsProps {
    menuData: MenuRouteItem[];
    userId?: number | string; // passed in from MainLayout
    isExternalUser?: boolean;
}

function findMenuItemByPath(
    menu: (MenuRouteItem | MenuRoute)[],
    path: string
): MenuRouteItem | MenuRoute | undefined {
    for (const item of menu) {
        if ("to" in item && item.to === path) return item;
        if ("children" in item && item.children) {
            const found = findMenuItemByPath(
                item.children as (MenuRouteItem | MenuRoute)[],
                path
            );
            if (found) return found;
        }
    }
    return undefined;
}

function buildHref(path: string, userId?: number | string): string | UrlObject {
    const uid =
        userId != null && userId !== "" && userId !== 0
            ? String(userId)
            : undefined;
    if (!uid) return path;

    try {
        const url = new URL(path, "http://local"); // base only for parsing
        const q = new URLSearchParams(url.search);
        if (!q.has("userId")) q.set("userId", uid);
        const qs = q.toString();
        return `${url.pathname}${qs ? `?${qs}` : ""}`;
    } catch {
        const obj: UrlObject = { pathname: path, query: { userId: uid } };
        return obj;
    }
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
    menuData,
    userId,
    isExternalUser,
}) => {
    const pathname = usePathname() ?? "";
    const normalizedPath = pathname?.replace(/\/+$/, "");
    if (normalizedPath === "" || normalizedPath === "/dashboard") return null;

    const segments = normalizedPath?.replace(/^\/+/, "").split("/") || [];
    const homeCrumb = {
        label: "Home",
        to: isExternalUser ? "/externalLoginDashboard" : "/dashboard",
    };

    let trail: { label: string; to?: string }[] = [];
    let pathSoFar = "";
    for (let i = 0; i < segments.length; i++) {
        pathSoFar += "/" + segments[i];
        const menuItem = findMenuItemByPath(menuData, pathSoFar);

        if (menuItem) {
            const label =
                "label" in menuItem && menuItem.label
                    ? menuItem.label
                    : "name" in menuItem && menuItem.name
                      ? menuItem.name
                      : segments[i].charAt(0).toUpperCase() +
                        segments[i].slice(1);

            trail.push({
                label,
                to: "to" in menuItem ? menuItem.to : undefined,
            });
        } else {
            // fallback label
            const cleanLabel = segments[i]
                .replace(/-/g, " ")
                .split(" ")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ");

            // overrides
            let overridePath = pathSoFar;
            if (cleanLabel === "Request") overridePath = "/request/manage";
            if (cleanLabel === "Trip") overridePath = "/trip/manage";

            trail.push({ label: cleanLabel, to: overridePath });
        }
    }

    if (trail.length > 0) {
        trail = [homeCrumb, ...trail];
    }

    trail = trail.filter((c) => c && c.label && c.label.trim() !== "");

    const nonLinkableLabels = new Set(["Admin", "Staff", "Documentation"]);

    return (
        <nav aria-label="breadcrumb" style={{ margin: "0 0 1rem 0" }}>
            <ol
                style={{
                    display: "flex",
                    gap: 8,
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                }}
            >
                {trail.map((crumb, idx) => {
                    const isCurrent = idx === trail.length - 1;
                    const canLink =
                        Boolean(crumb.to) &&
                        !isCurrent &&
                        !nonLinkableLabels.has(crumb.label);

                    if (!canLink) {
                        return (
                            <li
                                key={idx}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                                className="p-0 m-0 breadcrumb-item-custom"
                                aria-current={isCurrent ? "page" : undefined}
                            >
                                <span className="breadcrumb-text-custom me-2">
                                    {crumb.label}
                                </span>
                                {idx < trail.length - 1 && (
                                    <span className="breadcrumb-separator-custom">
                                        /
                                    </span>
                                )}
                            </li>
                        );
                    }

                    const href: string | UrlObject = buildHref(
                        crumb.to!,
                        userId
                    );

                    return (
                        <li
                            key={idx}
                            style={{ display: "flex", alignItems: "center" }}
                            className="p-0 m-0 breadcrumb-item-custom"
                        >
                            <Link
                                href={href}
                                className="breadcrumb-link-custom"
                                style={{ marginRight: "0.5rem" }}
                            >
                                {crumb.label}
                            </Link>
                            {idx < trail.length - 1 && (
                                <span className="breadcrumb-separator-custom">
                                    /
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;
